import { motion } from 'framer-motion';
import { useId } from 'react';

interface WinnerCrownProps {
  size?: number;
  className?: string;
  /** When false, skip built-in motion (parent controls animation). */
  animate?: boolean;
}

/** Premium gold winner crown — sits on avatar */
export default function WinnerCrown({ size = 32, className = '', animate = true }: WinnerCrownProps) {
  const id = useId().replace(/:/g, '');
  const h = Math.round(size * 0.82);

  const svg = (
    <svg width={size} height={h} viewBox="0 0 64 52" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <defs>
          <linearGradient id={`${id}-gold`} x1="8" y1="4" x2="56" y2="48" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FFF8DC" />
            <stop offset="0.25" stopColor="#F5D76E" />
            <stop offset="0.55" stopColor="#D4AF37" />
            <stop offset="1" stopColor="#8B6914" />
          </linearGradient>
          <linearGradient id={`${id}-shine`} x1="20" y1="8" x2="44" y2="28" gradientUnits="userSpaceOnUse">
            <stop stopColor="#FFFFFF" stopOpacity="0.65" />
            <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
          <radialGradient id={`${id}-ruby`} cx="0.35" cy="0.35" r="0.65">
            <stop stopColor="#FF6B8A" />
            <stop offset="1" stopColor="#9B1B30" />
          </radialGradient>
          <filter id={`${id}-glow`} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodColor="#000" floodOpacity="0.55" />
            <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#D4AF37" floodOpacity="0.45" />
          </filter>
        </defs>

        <g filter={`url(#${id}-glow)`}>
          {/* Base band */}
          <path
            d="M6 38h52l-2 10H8L6 38z"
            fill={`url(#${id}-gold)`}
            stroke="#5A4210"
            strokeWidth="0.8"
          />
          <path d="M10 41h44v2H10v-2z" fill="#FFF8DC" opacity="0.35" />

          {/* Crown body */}
          <path
            d="M8 38L14 18l8 12 10-16 10 16 8-12 6 20H8z"
            fill={`url(#${id}-gold)`}
            stroke="#5A4210"
            strokeWidth="0.9"
            strokeLinejoin="round"
          />

          {/* Highlight sheen */}
          <path d="M14 20l6 10 4-8 4 8 5-9 4 14H16L14 20z" fill={`url(#${id}-shine)`} opacity="0.55" />

          {/* Jewels */}
          <circle cx="32" cy="24" r="3.2" fill={`url(#${id}-ruby)`} stroke="#F5E6A8" strokeWidth="0.6" />
          <circle cx="20" cy="28" r="2.2" fill="#1E3A8A" stroke="#93C5FD" strokeWidth="0.5" />
          <circle cx="44" cy="28" r="2.2" fill="#166534" stroke="#86EFAC" strokeWidth="0.5" />

          {/* Point tips */}
          <circle cx="14" cy="18" r="2" fill="#F5E6A8" stroke="#8B6914" strokeWidth="0.5" />
          <circle cx="32" cy="8" r="2.2" fill="#F5E6A8" stroke="#8B6914" strokeWidth="0.5" />
          <circle cx="50" cy="18" r="2" fill="#F5E6A8" stroke="#8B6914" strokeWidth="0.5" />
        </g>
      </svg>
  );

  if (!animate) {
    return <div className={`tp-winner-crown-badge ${className}`}>{svg}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.4, rotate: -8 }}
      animate={{
        opacity: 1,
        y: [0, -2, 0],
        scale: 1,
        rotate: 0,
      }}
      transition={{
        opacity: { duration: 0.25 },
        scale: { type: 'spring', stiffness: 420, damping: 18 },
        rotate: { type: 'spring', stiffness: 420, damping: 18 },
        y: { duration: 2.2, repeat: Infinity, ease: 'easeInOut', delay: 0.35 },
      }}
      className={`tp-winner-crown-badge ${className}`}
      aria-hidden
    >
      {svg}
    </motion.div>
  );
}
